/**
 * Вложения багфикс-агента и обратной связи: файл уходит из браузера прямо в API v3 (POST /assets/upload,
 * публичный ассет), затем маршрут модуля получает только его id и сохраняет описание вложения.
 * Next.js и медиа-сервер байты не видят — нет лимита тела функции Vercel и файлов на диске экземпляра.
 */
import { getWorkspaces, uploadAsset } from "@/lib/api-v3"
import { getActiveAccount, getTokens, getWorkspaceForAccount } from "@/lib/auth"
import { attachmentKind, type Attachment, type AttachmentKind } from "../shared/types"

const V3_TYPE: Record<AttachmentKind, string> = { image: "image", video: "video", audio: "file", file: "file" }

export class UploadError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

async function workspaceId(token: string): Promise<number> {
  const account = getActiveAccount()
  const cached = account ? getWorkspaceForAccount(account.id) : null
  if (cached?.workspaceId) return cached.workspaceId
  const [first] = await getWorkspaces(token)
  if (!first) throw new UploadError(403, "Нет рабочего пространства для загрузки файла")
  return first.workspaceId
}

function uploadMessage(e: unknown, name: string): string {
  const msg = (e as Error)?.message || ""
  if (/^HTTP 413/.test(msg)) return `${name}: файл слишком большой для хранилища`
  if (/^HTTP 401/.test(msg)) return "Нет авторизации — войдите заново"
  if (msg === "Загрузка отменена") return msg
  return `Не удалось загрузить ${name}${msg ? ` (${msg})` : ""}`
}

/** Загрузить файл в v3 и зарегистрировать во `endpoint` (POST { assetId, name, mime } → { attachment }) */
export async function uploadViaV3(endpoint: string, file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<Attachment> {
  const token = getTokens()?.accessToken
  if (!token) throw new UploadError(401, "Нет авторизации — войдите заново")
  const name = file.name || "file"
  const asset = await uploadAsset(token, {
    name,
    type: V3_TYPE[attachmentKind(file.type, name)],
    workspaceId: await workspaceId(token),
    file,
    isPublic: true,
    onProgress: (p) => onProgress?.(Math.min(0.99, p / 100)),
    signal,
  }).catch((e) => {
    throw new UploadError(0, uploadMessage(e, name))
  })
  const res = await fetch(endpoint, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ assetId: asset.id, name, mime: file.type }),
    signal,
  })
  const body = (await res.json().catch(() => ({}))) as { attachment?: Attachment; error?: string }
  if (!res.ok || !body.attachment) throw new UploadError(res.status, body.error || `Не удалось сохранить ${name}`)
  onProgress?.(1)
  return body.attachment
}
